import {
  SB_KIT_TO_HOST,
  SB_KIT_VERSION,
  SB_THEME_MODES,
  type SbEnvelope,
  type SbFieldInfo,
  type SbIvyState,
  type SbMessage,
  type SbStage,
  type StoryboardHistory,
  type SbThemeMode,
  type StoryboardInputBody,
} from '@agentic-ivy/shared-types';

type KitToHostType = (typeof SB_KIT_TO_HOST)[number];
type HostToKit = Exclude<SbMessage, { type: KitToHostType }>;
type Payload<T extends SbMessage['type']> = Extract<SbMessage, { type: T }>['payload'];

export interface HostTheme {
  mode: SbThemeMode;
  dark: boolean;
}

export interface BridgeEvent {
  source: unknown;
  origin: string;
  data: unknown;
}

export interface BridgeTarget {
  postMessage(message: unknown, targetOrigin: string): void;
}

export interface BridgeHostDeps {
  frame(): BridgeTarget | null;
  frameOrigin(): string | null;
  version(): number | null;
  signMedia(keys: string[]): Promise<Record<string, string>>;
  postInput(body: StoryboardInputBody): Promise<{ inputId: string }>;
  /** The page is ready: its fields, and (from a served page) its stage and the history of every page. */
  onReady?(fields: SbFieldInfo[], page: { stage: SbStage | null; history: StoryboardHistory | null }): void;
  onPendingChange?(pending: number): void;
  onSent?(inputId: string, body: StoryboardInputBody): void;
  /** The page's "See all looks" was pressed. Having this is what offers it. */
  onSeeLooks?(): void;
  openLink?(url: string): void;
  /** The app's theme, sent to the page when it is ready. */
  theme?(): HostTheme | null;
  /** The page's theme switch was used: change the app's theme. */
  onSetTheme?(mode: SbThemeMode): void;
  /** A stage tab or History item in the page: open that version. */
  onNavigate?(version: number): void;
  /** The app draws the page's header itself (title, page menu, progress, theme), so the page hides its own. */
  ownsChrome?: boolean;
  /** Surfaces and text the page borrows for the current theme (sb:host style); null lends none. */
  hostStyle?(): Record<string, string> | null;
  /** The app holds the page in a panel of its own, so the page's ground is plain, without its dots. */
  plainGround?: boolean;
  /** What Ivy is doing, for a host that has her: told to the page when it is ready (with `fresh`), then on every change. */
  ivy?(): IvyNow | null;
}

export interface IvyNow {
  state: SbIvyState;
  version?: number;
  fresh?: boolean;
}

export interface BridgeHost {
  handle(event: BridgeEvent): Promise<void>;
  seek(t: number): void;
  theme(t: HostTheme): void;
  ivy(now: Omit<IvyNow, 'fresh'>): void;
  lookChosen(look: Payload<'sb:look-chosen'>): void;
}

export function parseKitMessage(data: unknown): SbEnvelope<KitToHostType> | null {
  if (!data || typeof data !== 'object') return null;
  const msg = data as Partial<SbEnvelope>;
  if (msg.sb !== SB_KIT_VERSION || typeof msg.type !== 'string') return null;
  if (!(SB_KIT_TO_HOST as readonly string[]).includes(msg.type)) return null;
  if (!msg.payload || typeof msg.payload !== 'object') return null;
  return msg as SbEnvelope<KitToHostType>;
}

const errorMessage = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong');

const IMAGE = /^(https:\/\/|media:)/;

/** The page's words and pictures for the touched fields only: strings, and pictures that are https or media: keys. */
function words(map: unknown, touched: string[], keep: (v: string) => boolean = () => true): Record<string, string> | undefined {
  if (!map || typeof map !== 'object') return undefined;
  const out = Object.fromEntries(touched.flatMap((n) => {
    const v = (map as Record<string, unknown>)[n];
    return typeof v === 'string' && v.length <= 2048 && keep(v) ? [[n, v]] : [];
  }));
  return Object.keys(out).length ? out : undefined;
}

export function createBridgeHost(deps: BridgeHostDeps): BridgeHost {
  const post = (msg: HostToKit) => {
    const frame = deps.frame();
    const origin = deps.frameOrigin();
    if (!frame || !origin) return;
    frame.postMessage({ sb: SB_KIT_VERSION, type: msg.type, payload: msg.payload }, origin);
  };

  let pageReady = false;
  const offer = () => {
    const style = deps.hostStyle?.() ?? null;
    post({
      type: 'sb:host',
      payload: { navigate: !!deps.onNavigate, ...(deps.onSeeLooks ? { looks: true } : {}), ...(deps.ownsChrome ? { chrome: 'host' as const } : {}), ...(style ? { style } : {}), ...(deps.plainGround ? { ground: 'plain' as const } : {}) },
    });
  };

  const resolve = async ({ id, keys }: Payload<'sb:resolve'>) => {
    if (typeof id !== 'string' || !Array.isArray(keys)) return;
    try {
      const urls = keys.length ? await deps.signMedia(keys) : {};
      post({ type: 'sb:resolved', payload: { id, urls } });
    } catch (err) {
      post({ type: 'sb:error', payload: { message: `Could not load media: ${errorMessage(err)}` } });
    }
  };

  const send = async ({ fields, touched, lock, summaries, images }: Payload<'sb:send'>) => {
    const storyboardVersion = deps.version();
    if (storyboardVersion == null) {
      post({ type: 'sb:error', payload: { message: 'No storyboard version is open' } });
      return;
    }
    const body: StoryboardInputBody = { storyboardVersion, fields: fields ?? {}, touched: touched ?? [] };
    if (lock) body.lock = true;
    const said = words(summaries, body.touched);
    const pictures = words(images, body.touched, (v) => IMAGE.test(v));
    if (said) body.summaries = said;
    if (pictures) body.images = pictures;
    try {
      const { inputId } = await deps.postInput(body);
      post({ type: 'sb:sent', payload: { inputId } });
      deps.onPendingChange?.(0);
      deps.onSent?.(inputId, body);
    } catch (err) {
      post({ type: 'sb:error', payload: { message: errorMessage(err) } });
    }
  };

  return {
    async handle(event) {
      const frame = deps.frame();
      if (!frame || event.source !== frame) return;
      const origin = deps.frameOrigin();
      if (origin && origin !== '*' && event.origin !== origin) return;
      const msg = parseKitMessage(event.data);
      if (!msg) return;
      switch (msg.type) {
        case 'sb:ready': {
          const ready = msg.payload as Payload<'sb:ready'>;
          deps.onReady?.(ready.fields ?? [], { stage: ready.stage ?? null, history: ready.history ?? null });
          const theme = deps.theme?.();
          if (theme) post({ type: 'sb:theme', payload: theme });
          pageReady = true;
          offer();
          const now = deps.ivy?.();
          if (now) post({ type: 'sb:ivy', payload: now });
          return;
        }
        case 'sb:navigate': {
          const version = Number((msg.payload as Payload<'sb:navigate'>).version);
          if (Number.isInteger(version) && version > 0) deps.onNavigate?.(version);
          return;
        }
        case 'sb:set-theme': {
          const mode = (msg.payload as Payload<'sb:set-theme'>).mode;
          if ((SB_THEME_MODES as readonly unknown[]).includes(mode)) deps.onSetTheme?.(mode);
          return;
        }
        case 'sb:see-looks':
          deps.onSeeLooks?.();
          return;
        case 'sb:changed':
          deps.onPendingChange?.(Number((msg.payload as Payload<'sb:changed'>).pending) || 0);
          return;
        case 'sb:resolve':
          return resolve(msg.payload as Payload<'sb:resolve'>);
        case 'sb:send':
          return send(msg.payload as Payload<'sb:send'>);
        case 'sb:open': {
          const url = String((msg.payload as Payload<'sb:open'>).url ?? '');
          if (/^https:\/\//.test(url)) (deps.openLink ?? ((u: string) => window.open(u, '_blank', 'noopener')))(url);
          return;
        }
      }
    },
    seek(t) {
      post({ type: 'sb:seek', payload: { t } });
    },
    lookChosen(look) {
      post({ type: 'sb:look-chosen', payload: look });
    },
    theme(t) {
      post({ type: 'sb:theme', payload: t });
      // The borrowed surfaces are the theme's own: lend the new theme's once the page is listening.
      if (pageReady && deps.hostStyle) offer();
    },
    ivy(now) {
      if (pageReady) post({ type: 'sb:ivy', payload: now });
    },
  };
}
